import { inject, InjectionToken } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import type { RequestParams } from "../shared/interfaces/request-params.interface";
import type { PageInfo } from "../shared/interfaces/page-result.interface";
import type { JsonPatchData } from "../shared/interfaces/json-patch-data.interface";

/**
 * API 基础地址（InjectionToken）
 *
 * 在 app.config.ts 或 app.component.ts 的 providers 中配置：
 *   { provide: API_BASE_URL, useValue: 'https://api.example.com' }
 */
export const API_BASE_URL = new InjectionToken<string>("API_BASE_URL");

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObject = Record<string, any>;

/**
 * 通用 CRUD Service 基类
 *
 * 对应 study-plan 项目 `api/BaseService.ts`，
 * 将 uni-app 的 request/requestPage 替换为 Angular HttpClient。
 *
 * 用法：
 *   @Injectable({ providedIn: 'root' })
 *   export class SubjectService extends BaseService<Subject> {
 *     constructor() { super('subject'); }
 *   }
 */
export abstract class BaseService<TEntity> {
  protected readonly http = inject(HttpClient);
  protected readonly apiBaseUrl =
    inject(API_BASE_URL, { optional: true }) || "";

  /** API 路径前缀，例如 "subject" → 请求 /subject/... */
  protected readonly apiPrefix: string;

  constructor(apiPrefix: string) {
    this.apiPrefix = apiPrefix;
  }

  // ============================================================
  //  查询
  // ============================================================

  /** 按 ID 查询单条 */
  getEntityById(id: number, params: RequestParams = {}): Observable<TEntity[]> {
    return this.http
      .get<TEntity[]>(`${this.apiBaseUrl}/${this.apiPrefix}/getById/${id}`, {
        params: this.buildParams(params),
      })
      .pipe(map((res) => this.unwrap<TEntity[]>(res)));
  }

  /** 分页列表 */
  getPageList(
    params: RequestParams = {},
    filter?: Partial<TEntity>,
  ): Observable<PageInfo<TEntity>> {
    const merged = { ...params, ...(filter as AnyObject) };
    return this.http
      .get<PageInfo<TEntity>>(`${this.apiBaseUrl}/${this.apiPrefix}/getall`, {
        params: this.buildParams(merged),
      })
      .pipe(map((res) => this.unwrap<PageInfo<TEntity>>(res)));
  }

  /** 全量列表（不分页） */
  getEntityList(params: RequestParams = {}): Observable<TEntity[]> {
    return this.http
      .get<TEntity[]>(`${this.apiBaseUrl}/${this.apiPrefix}/getall`, {
        params: this.buildParams(params),
      })
      .pipe(map((res) => this.unwrap<TEntity[]>(res)));
  }

  /** 计数 */
  getCount(params: RequestParams = {}): Observable<number> {
    return this.http
      .get<number>(`${this.apiBaseUrl}/${this.apiPrefix}/getall`, {
        params: this.buildParams({ ...params, "@count": true }),
      })
      .pipe(map((res) => this.unwrap<number>(res)));
  }

  // ============================================================
  //  新增
  // ============================================================

  /** 添加单条 */
  addEntity(
    entity: Partial<TEntity>,
    relationships?: string,
  ): Observable<TEntity> {
    let url = `${this.apiBaseUrl}/${this.apiPrefix}/add`;
    if (relationships) {
      url += `?@relationships=${relationships}`;
    }
    return this.http
      .post<TEntity>(url, [entity])
      .pipe(map((res) => this.unwrap<TEntity>(res)));
  }

  /** 批量添加 */
  addEntities(
    entities: Partial<TEntity>[],
    relationships?: string,
  ): Observable<TEntity[]> {
    let url = `${this.apiBaseUrl}/${this.apiPrefix}/add`;
    if (relationships) {
      url += `?@relationships=${relationships}`;
    }
    return this.http
      .post<TEntity[]>(url, entities)
      .pipe(map((res) => this.unwrap<TEntity[]>(res)));
  }

  // ============================================================
  //  删除
  // ============================================================

  /** 按 ID 批量删除 */
  delEntitiesByIds(ids: number[]): Observable<number> {
    return this.http
      .post<number>(`${this.apiBaseUrl}/${this.apiPrefix}/delete`, ids)
      .pipe(map((res) => this.unwrap<number>(res)));
  }

  /** 删除单条 */
  delEntity(id: number, relationships?: string): Observable<number> {
    let url = `${this.apiBaseUrl}/${this.apiPrefix}/delete`;
    if (relationships) {
      url += `?@relationships=${relationships}`;
    }
    return this.http
      .post<number>(url, [id])
      .pipe(map((res) => this.unwrap<number>(res)));
  }

  // ============================================================
  //  编辑
  // ============================================================

  /** 编辑单条（全量更新） */
  editEntity(
    entity: Partial<TEntity>,
    relationships?: string,
  ): Observable<TEntity> {
    let url = `${this.apiBaseUrl}/${this.apiPrefix}/put`;
    if (relationships) {
      url += `?@relationships=${relationships}`;
    }
    return this.http
      .put<TEntity>(url, [entity])
      .pipe(map((res) => this.unwrap<TEntity>(res)));
  }

  /** 批量编辑（全量更新） */
  editEntities(
    entities: Partial<TEntity>[],
    relationships?: string,
  ): Observable<TEntity[]> {
    let url = `${this.apiBaseUrl}/${this.apiPrefix}/put`;
    if (relationships) {
      url += `?@relationships=${relationships}`;
    }
    return this.http
      .put<TEntity[]>(url, entities)
      .pipe(map((res) => this.unwrap<TEntity[]>(res)));
  }

  // ============================================================
  //  部分更新（JSON Patch / RFC 6902）
  // ============================================================

  /** 单条部分更新 */
  commonUpdate(
    id: number,
    jsonPatchData: JsonPatchData[],
  ): Observable<TEntity> {
    return this.http
      .post<TEntity>(
        `${this.apiBaseUrl}/${this.apiPrefix}/PatchById`,
        jsonPatchData,
        {
          params: new HttpParams().set("id", id),
        },
      )
      .pipe(map((res) => this.unwrap<TEntity>(res)));
  }

  /** 批量部分更新 */
  commonUpdates(
    ids: number[],
    jsonPatchData: JsonPatchData[],
  ): Observable<TEntity[]> {
    return this.http
      .post<TEntity[]>(
        `${this.apiBaseUrl}/${this.apiPrefix}/PatchByIds`,
        jsonPatchData,
        {
          params: new HttpParams().set("ids", ids.join(",")),
        },
      )
      .pipe(map((res) => this.unwrap<TEntity[]>(res)));
  }

  // ============================================================
  //  工具方法
  // ============================================================

  /**
   * 将 RequestParams / filter 转为 Angular HttpParams
   *
   * 对应参考项目中的 `qs.stringify(params)`。
   * 过滤掉 undefined / null 值。
   */
  private buildParams(params: AnyObject): HttpParams {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      httpParams = httpParams.set(key, String(value));
    });

    return httpParams;
  }

  /**
   * 解包后端统一响应 { statuscode, items, ... } → 取 items
   *
   * 对应参考项目中 `request()` 返回 `resData.items` 的逻辑。
   * 如果后端不包这一层，派生类可以 override。
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected unwrap<T>(response: any): T {
    // 如果后端包了 ActionResult，取 items
    if (response && typeof response === "object" && "items" in response) {
      return response.items as T;
    }
    // 否则原样返回
    return response as T;
  }
}
